import React, { useEffect, useState } from 'react';
import { Link, withRouter, RouteComponentProps } from 'react-router-dom';
import { api } from '../../middleware/api';
import { END_POINTS } from '../../utils/constants/endPoints';
import SuccessToast from '../../components/successToast';
import ValidationErrorToast from '../../components/validationErrorToast';
import InterviewSidebar from './InterviewSidebar';
import { styles } from './interviewStyles';

interface MatchParams { id: string; }

interface LocationState {
  job?: any;
}

const InterviewJobDetail: React.FC<RouteComponentProps<MatchParams, any, LocationState>> = ({ match, location }) => {
  const id = match.params.id;
  const [job, setJob] = useState<any>(location?.state?.job || null);
  const [loading, setLoading] = useState(!location?.state?.job);
  const [copied, setCopied] = useState(false);

  // Editing state
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      let activeJob = job;

      if (!activeJob) {
        const jobsResponse = await api.get(END_POINTS.interviewJobs, {});
        const jobs = Array.isArray(jobsResponse) ? jobsResponse : (jobsResponse?.results || []);
        activeJob = jobs.find((item: any) => String(item.id) === String(id));
        setJob(activeJob || { id, title: 'Interview Job', description: '' });
      }

    } catch (e: any) {
      setJob({ id, title: 'Interview Job', description: '' });
      ValidationErrorToast('Warning', 'Job details could not be loaded, but you can still manage interviews.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  const startEditing = () => {
    setEditTitle(job?.title || '');
    setEditDescription(job?.description || '');
    setEditing(true);
  };

  const cancelEditing = () => {
    setEditing(false);
  };

  const saveEdits = async () => {
    setSaving(true);
    try {
      const updated = await api.patch(`${END_POINTS.interviewJobs}${id}/`, { title: editTitle, description: editDescription });
      const updatedJob = updated?.data || updated;
      setJob({ ...job, ...updatedJob, title: editTitle, description: editDescription });
      setEditing(false);
      SuccessToast('Saved', 'Job updated successfully.');
    } catch (e: any) {
      ValidationErrorToast('Error', 'Could not save changes.');
    } finally {
      setSaving(false);
    }
  };

  const publicToken = job?.publicToken || job?.public_token;
  const publicInterviewLink = publicToken ? `${window.location.origin}/interview-simulator/job/${publicToken}` : '';

  const copyPublicLink = async () => {
    if (!publicInterviewLink) return;

    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(publicInterviewLink);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = publicInterviewLink;
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }

      setCopied(true);
      SuccessToast('Copied', 'Interview link copied');
      setTimeout(() => setCopied(false), 1800);
    } catch (e: any) {
      ValidationErrorToast('Copy failed', 'Could not copy the interview link.');
    }
  };


  if (loading) {
    return (
      <InterviewSidebar>
        <div style={styles.emptyState}>
          <div className="spinner-border" role="status" style={{ color: '#0065FF' }}>
            <span className="sr-only">Loading...</span>
          </div>
          <p style={{ marginTop: '20px', ...styles.secondaryText }}>Loading job details...</p>
        </div>
      </InterviewSidebar>
    );
  }

  return (
    <InterviewSidebar>
      <div style={{ marginBottom: '16px' }}>
        <Link to="/interview-simulator/jobs" style={{ color: '#0065FF', textDecoration: 'none', fontSize: '14px' }}>← Back to Jobs</Link>
      </div>

      {/* Job Info Card */}
      <div style={{ ...styles.card, marginBottom: '24px' }}>
        {editing ? (
          <>
            <label style={{ fontWeight: 600, fontSize: '14px', color: '#141414', marginBottom: '6px', display: 'block' }}>Title</label>
            <input
              style={{ ...styles.modalInput, marginBottom: '12px' }}
              value={editTitle}
              onChange={e => setEditTitle(e.target.value)}
              placeholder="Job title"
            />
            <label style={{ fontWeight: 600, fontSize: '14px', color: '#141414', marginBottom: '6px', display: 'block' }}>Description / Requirements</label>
            <textarea
              style={{ ...styles.modalInput, minHeight: '120px', resize: 'vertical' } as any}
              value={editDescription}
              onChange={e => setEditDescription(e.target.value)}
              placeholder="Description and requirements"
            />
            <div style={{ display: 'flex', gap: '10px', marginTop: '14px' }}>
              <button style={{ ...styles.button, ...styles.primaryButton }} onClick={saveEdits} disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </button>
              <button style={{ ...styles.button, ...styles.secondaryButton }} onClick={cancelEditing} disabled={saving}>
                Cancel
              </button>
            </div>
          </>
        ) : (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <h3 style={styles.cardTitle}>{job?.title || 'Interview Job'}</h3>
                <p style={styles.secondaryText}>{job?.description || 'No description provided.'}</p>
              </div>
              <button
                style={{ ...styles.button, ...styles.secondaryButton, whiteSpace: 'nowrap', marginLeft: '16px' } as any}
                onClick={startEditing}
              >
                Edit
              </button>
            </div>
          </>
        )}
      </div>

      {/* Public Interview Link Card */}
      <div style={{ ...styles.card, marginBottom: '24px' }}>
        <h3 style={styles.cardTitle}>Public Interview Link</h3>
        <p style={styles.secondaryText}>Share this standard link with candidates so they can interview directly for this job without signing in.</p>
        {publicInterviewLink ? (
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              style={{ ...styles.modalInput, flex: 1, minWidth: '260px', marginTop: 0 }}
              value={publicInterviewLink}
              readOnly
              onFocus={e => e.currentTarget.select()}
            />
            <button style={{ ...styles.button, ...styles.primaryButton }} onClick={copyPublicLink}>
              {copied ? 'Copied' : 'Copy Link'}
            </button>
          </div>
        ) : (
          <p style={{ ...styles.secondaryText, color: '#dc3545' }}>Public link will appear after the backend public-link deploy is live.</p>
        )}
      </div>

      {/* Start Interview Card */}
      <div style={{ ...styles.card, marginBottom: '24px' }}>
        <h3 style={styles.cardTitle}>Start Interview</h3>
        <p style={styles.secondaryText}>Go through the candidate details and resume screen, then launch the AI interview simulator.</p>
        <Link
          to={{
            pathname: `/interview-simulator/jobs/${id}/start`,
            state: { job },
          }}
          style={{ ...styles.button, ...styles.primaryButton, textDecoration: 'none', display: 'inline-block' } as any}
        >
          Interview
        </Link>
      </div>

    </InterviewSidebar>
  );
};

export default withRouter(InterviewJobDetail);
